import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineCalculator } from 'react-icons/hi2';
import ModalLayout from '../layouts/ModalLayout';
import {
  businessBaseSubscriptionPrices,
  businessVariableSubscriptionPrices,
  communitySubscriptionPrices,
} from '../../../utils/BusinessFeatureUtils';
import { BusinessSubscriptionType, SubscriptionType } from '../../../types/misc';

interface CostCalculatorModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialSubscription?: BusinessSubscriptionType;
}

interface CommunityCounts {
  [SubscriptionType.FREE]: string;
  [SubscriptionType.BASE]: string;
  [SubscriptionType.PRO]: string;
}

const subscriptionLabels: Record<BusinessSubscriptionType, string> = {
  [BusinessSubscriptionType.STARTER]: 'Starter',
  [BusinessSubscriptionType.PROFESSIONAL]: 'Professional',
};

const communityTierLabels: Record<SubscriptionType, string> = {
  [SubscriptionType.FREE]: 'Free',
  [SubscriptionType.BASE]: 'Base',
  [SubscriptionType.PRO]: 'Pro',
};

const formatEuro = (amount: number) =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);

export default function CostCalculatorModal({
  open,
  setOpen,
  initialSubscription = BusinessSubscriptionType.STARTER,
}: CostCalculatorModalProps): JSX.Element {
  const { t } = useTranslation();
  const [subscription, setSubscription] = useState<BusinessSubscriptionType>(initialSubscription);
  const [memberCountInput, setMemberCountInput] = useState<string>('');
  const memberCount = parseInt(memberCountInput, 10) || 0;
  const [communityCounts, setCommunityCounts] = useState<CommunityCounts>({
    [SubscriptionType.FREE]: '0',
    [SubscriptionType.BASE]: '0',
    [SubscriptionType.PRO]: '0',
  });

  const basePrice = businessBaseSubscriptionPrices[subscription];
  const freeMembers = 25; // first 25 members are free
  const billedMembers = Math.max(0, memberCount - freeMembers);
  const variablePrice = businessVariableSubscriptionPrices[subscription] * billedMembers;
  const communityPrice = Object.entries(communityCounts).reduce((sum, [tier, countStr]) => {
    const count = parseInt(countStr as string, 10) || 0;
    return sum + communitySubscriptionPrices[tier as SubscriptionType] * count;
  }, 0);
  const totalPrice = basePrice + variablePrice + communityPrice;

  const handleCommunityCountChange = (tier: SubscriptionType, value: number) => {
    setCommunityCounts((prev) => ({ ...prev, [tier]: String(Math.max(0, value)) }));
  };

  const communityRows = [SubscriptionType.FREE, SubscriptionType.BASE, SubscriptionType.PRO];

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="relative transform overflow-hidden rounded-[12px] bg-secondary-50 px-6 py-6 text-left transition-all sm:my-8 sm:w-full sm:max-w-xl">

      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100">
          <HiOutlineCalculator className="h-5 w-5 text-primary-900" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-primary-900">
            {t('component.modal.costCalculator.title')}
          </h2>
          <p className="text-sm text-primary-900/60">
            {t('component.modal.costCalculator.subtitle')}
          </p>
        </div>
      </div>

      {/* Subscription type */}
      <div className="mb-5">
        <label className="mb-2 block text-sm font-medium text-primary-900">
          {t('component.modal.costCalculator.subscriptionType')}
        </label>
        <div className="grid grid-cols-2 gap-2">
          {Object.values(BusinessSubscriptionType).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setSubscription(type)}
              className={`relative rounded-xl border-2 p-4 text-left text-sm font-medium transition-all duration-300 group ${
                subscription === type
                  ? 'border-primary-900 bg-secondary-200 bg-opacity-10'
                  : 'border-secondary-200 bg-secondary-200 bg-opacity-30 hover:border-primary-900 hover:bg-opacity-40'
              }`}>
              <span className="block font-bold text-primary-900">{subscriptionLabels[type]}</span>
              <span className="mt-0.5 block text-xs font-normal text-primary-900/60">
                {formatEuro(businessBaseSubscriptionPrices[type])}{' '}
                {t('component.modal.costCalculator.perMonth')}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Member count */}
      <div className="mb-5">
        <label className="mb-2 block text-sm font-medium text-primary-900">
          {t('component.modal.costCalculator.memberCount')}
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            value={memberCountInput}
            placeholder="0"
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || /^\d+$/.test(val)) setMemberCountInput(val);
            }}
            onBlur={() => {
              if (memberCountInput === '') setMemberCountInput('');
            }}
            className="w-full rounded-xl border border-secondary-200 bg-white px-4 py-2.5 text-sm text-primary-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <p className="mt-1 text-xs text-primary-900/50">
          {formatEuro(businessVariableSubscriptionPrices[subscription])}{' '}
          {t('component.modal.costCalculator.perMember')}
          <span className="ml-2 text-xs text-primary-900/50">· {t('component.modal.costCalculator.first25Free')}</span>
        </p>
      </div>

      {/* Community counts */}
      <div className="mb-6">
        <label className="mb-2 block text-sm font-medium text-primary-900">
          {t('component.modal.costCalculator.communities')}
        </label>
        <div className="overflow-hidden rounded-xl border border-secondary-200">
          {communityRows.map((tier, idx) => (
            <div
              key={tier}
              className={`flex items-center justify-between gap-4 px-4 py-3 ${
                idx < communityRows.length - 1 ? 'border-b border-secondary-200' : ''
              }`}>
              <div>
                <p className="text-sm font-medium text-primary-900">
                  {communityTierLabels[tier]}
                </p>
                <p className="text-xs text-primary-900 text-opacity-50">
                  {communitySubscriptionPrices[tier] === 0
                    ? t('component.modal.costCalculator.free')
                    : `${formatEuro(communitySubscriptionPrices[tier])} ${t('component.modal.costCalculator.perCommunityMonth')}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleCommunityCountChange(tier, (parseInt(communityCounts[tier], 10) || 0) - 1)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary-100 text-lg font-medium text-primary-900 transition-colors hover:bg-secondary-200 disabled:opacity-30"
                  disabled={(parseInt(communityCounts[tier], 10) || 0) === 0}>
                  −
                </button>

                <input
                  type="number"
                  min={0}
                  value={communityCounts[tier]}
                  placeholder="0"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d+$/.test(val)) setCommunityCounts((prev) => ({ ...prev, [tier]: val }));
                  }}
                  onBlur={() => {
                    if (communityCounts[tier] === '') setCommunityCounts((prev) => ({ ...prev, [tier]: '0' }));
                  }}
                  className="max-w-16 rounded-lg border border-secondary-200 px-2 py-1 text-center text-sm text-primary-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />

                <button
                  type="button"
                  onClick={() => handleCommunityCountChange(tier, (parseInt(communityCounts[tier], 10) || 0) + 1)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary-100 text-lg font-medium text-primary-900 transition-colors hover:bg-secondary-200">
                  +
                </button>
              </div> 
            </div>
          ))}
        </div>
      </div>

      {/* Cost breakdown */}
      <div className="rounded-xl bg-primary-100 p-4">
        <p className="mb-3 text-sm font-semibold text-primary-900">
          {t('component.modal.costCalculator.breakdown')}
        </p>
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-primary-900">
            <span className="text-opacity-70">
              {t('component.modal.costCalculator.baseFee')} ({subscriptionLabels[subscription]})
            </span>
            <span className="font-medium">{formatEuro(basePrice)}</span>
          </div>
          {memberCount > 0 && (
            <div className="flex justify-between text-sm text-primary-900">
              <span className="text-primary-900/70">
                {memberCount} {t('component.modal.costCalculator.members')}
                {billedMembers > 0 ? (
                  <>&nbsp;({billedMembers} {t('component.modal.costCalculator.billed')})</>
                ) : (
                  <>&nbsp;({t('component.modal.costCalculator.first25Free')})</>
                )}
                &nbsp;× {formatEuro(businessVariableSubscriptionPrices[subscription])}
              </span>
              <span className="font-medium">{formatEuro(variablePrice)}</span>
            </div>
          )} 
          {communityRows
            .filter((tier) => (parseInt(communityCounts[tier], 10) || 0) > 0)
            .map((tier) => (
              <div key={tier} className="flex justify-between text-sm text-primary-900">
                <span className="text-primary-900/70">
                  {communityCounts[tier]} {communityTierLabels[tier]}{' '}
                  {t('component.modal.costCalculator.communitiesLabel')}
                  {communitySubscriptionPrices[tier] > 0
                    ? ` × ${formatEuro(communitySubscriptionPrices[tier])}`
                    : ''}
                </span>
                <span className="font-medium">
                  {communitySubscriptionPrices[tier] === 0
                    ? t('component.modal.costCalculator.free')
                    : formatEuro(communitySubscriptionPrices[tier] * (parseInt(communityCounts[tier], 10) || 0))}
                </span>
              </div>
            ))} 
          <div className="mt-3 border-t border-primary-200 pt-3">
            <div className="flex justify-between">
              <span className="text-sm font-semibold text-primary-900">
                {t('component.modal.costCalculator.total')}
              </span>
              <span className="text-lg font-bold text-primary-900">{formatEuro(totalPrice)}</span>
            </div>
            <p className="mt-0.5 text-right text-xs text-primary-900/50">
              {t('component.modal.costCalculator.perMonthLabel')}
            </p>
          </div>
        </div>
      </div>
    </ModalLayout>
  );
}
